/**
 * V2 UPI Autopay — shared selection helpers and constants.
 *
 * Overview of branch changes vs master:
 * - **API:** `fetchMandateDetails` (mandate-details) feeds v2 setup; v1 `fetchAutoPayMethods` (mandates)
 *   remains for legacy `AutoPayMethodsPage`. Status polling accepts optional `upiPackageName` for UPI app setup.
 * - **Routes:** `/v2/sip/auto-pay/methods` (SetupAutoPay), `/v2/sip/auto-pay/methods/select` (method picker).
 *   Legacy `/sip/auto-pay/methods` is used when `isUpiIntentsFlowEnabled()` is false.
 * - **After first SIP UPI payment** (SIPUPIPaymentStatusPage): mandate-details decides whether to skip to
 *   autopay status or open SetupAutoPay.
 * - **Post-setup waiting:** UPI → `AutoPayUpiLinkWaitingPage`; net banking → `AutoPayLinkWaitingPage`.
 * - **Gating:** `isUpiIntentsFlowEnabled()` — app version ≥ MIN_APP_VERSION_UPI_INTENTS (or dev environment).
 * - **Mandate-details shape:** `bankAccounts` drives net-banking picker UI; `availableMandates` holds existing
 *   mandates (typed per row). Same `accountId` can have a UPI mandate while the user sets up net banking — never
 *   send the wrong `mandateId` to POST .../mandate (see `resolveMandateIdForAutopayMode`).
 * - **User's payment mode wins:** SIP-level `mandateType` is a default hint only; explicit UPI vs net banking
 *   selection is preserved in reconcile and setup payload (`getSetupMandateTypeFromAutopaySelection`).
 * - **Blocked UPI apps:** Product blocklist (`isBlockedUpiApp`) applies to payment intent and autopay pickers.
 */
import { InstalledUpiApp } from '../installedUpiIntents'
import {
  AutoPayBankAccount,
  AutoPayMandateDetails,
  AutoPayMandateType,
  AutoPayStatus,
  AutopayMethodSelection,
  AutopayPaymentMode,
  InvestPaymentMethodType,
  MandateDetailsResponse,
  SetupAutoPayMandateType,
  SetupAutoPayPayload
} from '../../types/mf-execution.types'
import { getDefaultInstalledUpiApp, getUpiAppSelectionId } from './paymentMethodSelection'
import { isBlockedUpiApp, resolveUpiAppDisplay } from './upiAppConfig'
import { getAppOStype } from '../utils'

export const AUTO_PAY_UPI_AMOUNT_THRESHOLD = 15000
export const UPI_AUTO_PAY_MAX_LIMIT = 15000
export const NET_BANKING_AUTO_PAY_MAX_LIMIT = 100000

/** Placeholders when net banking is required and no UPI apps are installed. */
const DISABLED_AUTOPAY_UPI_PLACEHOLDER_PACKAGES = ['com.google.android.apps.nbu.paisa.user'] as const

/** True when a UPI app must not be used for mandate setup (product blocklist). */
export function isExcludedAutopayUpiApp(packageName: string | null | undefined): boolean {
  const key = (packageName ?? '').trim().toLowerCase()
  if (!key) return false
  // TEMP: CRED Android / PhonePe iOS autopay issues. Delete these blocks to restore.
  if (
    getAppOStype()?.toLowerCase() !== 'ios' &&
    (key.includes('dreamplug') || key.includes('cred'))
  ) {
    return true
  }
  if (getAppOStype()?.toLowerCase() === 'ios' && key.includes('phonepe')) {
    return true
  }
  return isBlockedUpiApp(key)
}

/** Subset of native-installed apps eligible for v2 autopay; use everywhere autopay lists or defaults UPI apps. */
export function filterInstalledUpiAppsForAutopay(
  installedUpiApps: InstalledUpiApp[] | null | undefined
): InstalledUpiApp[] {
  return (installedUpiApps ?? []).filter((app) => !isExcludedAutopayUpiApp(app?.packageName))
}

/** Clears stale UPI selections restored from navigation state before reconcile/confirm. */
function isAutopayUpiSelectionExcluded(selection: AutopayMethodSelection | null | undefined): boolean {
  return selection?.mode === 'UPI' && isExcludedAutopayUpiApp(selection?.upiPackageName)
}

/** Greyed-out UPI rows when mandate requires net banking but no UPI intents are installed. */
export function getDisabledAutopayUpiPlaceholderApps(): InstalledUpiApp[] {
  return DISABLED_AUTOPAY_UPI_PLACEHOLDER_PACKAGES.map((packageName) => {
    const resolved = resolveUpiAppDisplay(packageName)
    return {
      packageName,
      name: resolved.name,
      appLogoUrl: resolved.logoUrl
    }
  })
}

/** Existing mandates from mandate-details — used for UPI/net-banking mandate id resolution, not bank list UI. */
export function getMandateAccountsFromDetails(
  mandateDetails: MandateDetailsResponse | null | undefined
): AutoPayMandateDetails[] {
  return mandateDetails?.availableMandates ?? []
}

/** Eligible bank accounts for net-banking rows and UPI linked-bank picker (replaces `accountsWithNoMandateSetup`). */
export function getBankAccountsFromDetails(
  mandateDetails: MandateDetailsResponse | null | undefined
): AutoPayBankAccount[] {
  return mandateDetails?.bankAccounts ?? []
}

/** SIP debit day of month from mandate-details (`sipDate`). */
export function getSipDateDayFromMandateDetails(mandateDetails: MandateDetailsResponse): number {
  return mandateDetails.sipDate
}

export function autoPayBankAccountToMandateDetails(bankAccount: AutoPayBankAccount): AutoPayMandateDetails {
  return {
    accountId: bankAccount.accountId,
    mandateId: null,
    mandateType: null,
    displayAccNumber: bankAccount.displayAccNumber ?? '',
    bankUrlLogo: bankAccount.bankUrlLogo ?? '',
    autoPayStatus: AutoPayStatus.SETUP_PENDING
  }
}

/** Normalizes mandate-details `mandateType` (handles API variants like `NETBANKING`). */
export function isUpiMandateType(mandateType?: AutoPayMandateType | string | null) {
  if (mandateType == null || mandateType === '') return false
  const normalized = String(mandateType).toUpperCase().replace(/_/g, '')
  return normalized === 'UPIAUTOPAY' || normalized === 'UPI'
}

export function isNetBankingMandateType(mandateType?: AutoPayMandateType | string | null) {
  if (mandateType == null || mandateType === '') return false
  return !isUpiMandateType(mandateType)
}

/** True when `availableMandates` has a net-banking mandate for this bank (shows "Active" in net-banking list). */
export function hasActiveNetBankingMandateForAccount(
  accountId: string,
  mandateAccounts: AutoPayMandateDetails[]
): boolean {
  return mandateAccounts.some(
    (mandate) => mandate?.accountId === accountId && isNetBankingMandateType(mandate?.mandateType)
  )
}

/**
 * Resolves UPI vs Net Banking for setup UI.
 * Priority: mandate-details `mandateType` → SIP amount threshold.
 */
export function resolveAutopayPaymentMode({
  mandateType,
  amount
}: {
  mandateType?: AutoPayMandateType | string | null
  amount: number
}): AutopayPaymentMode {
  if (mandateType != null && mandateType !== '') {
    return isUpiMandateType(mandateType) ? 'UPI' : 'NET_BANKING'
  }
  return amount < AUTO_PAY_UPI_AMOUNT_THRESHOLD ? 'UPI' : 'NET_BANKING'
}

/**
 * Max limit shown on Setup Autopay.
 * UPI always uses the UPI cap. Net banking uses mandate-details `maxLimit` only when SIP `mandateType`
 * is net banking; if the user picked net banking on a UPI-mandate SIP, use the hardcoded net-banking cap.
 */
export function resolveAutopayMaxLimit({
  paymentMode,
  mandateType,
  mandateMaxLimit,
}: {
  paymentMode: AutopayPaymentMode
  mandateType?: AutoPayMandateType | string | null
  mandateMaxLimit?: number
}): number {
  if (paymentMode === 'UPI') {
    return UPI_AUTO_PAY_MAX_LIMIT
  }
  if (isNetBankingMandateType(mandateType)) {
    return mandateMaxLimit ?? NET_BANKING_AUTO_PAY_MAX_LIMIT
  }
  return NET_BANKING_AUTO_PAY_MAX_LIMIT
}

export function getAutopayNetBankingSelectionId(accountId: string) {
  return `autopay-netbank:${accountId}`
}

export function mandateAccountToPaymentMethodRow(account: AutoPayMandateDetails): InvestPaymentMethodType {
  return {
    paymentMethodId: account?.accountId ?? '',
    displayAccNumber: account?.displayAccNumber ?? '',
    logoUrl: account?.bankUrlLogo ?? '',
    accountNumber: account?.accountId ?? ''
  } as InvestPaymentMethodType
}

export function bankAccountToPaymentMethodRow(account: AutoPayBankAccount): InvestPaymentMethodType {
  return {
    paymentMethodId: account?.accountId ?? '',
    displayAccNumber: account?.displayAccNumber ?? '',
    logoUrl: account?.bankUrlLogo ?? '',
    accountNumber: account?.accountId ?? ''
  } as InvestPaymentMethodType
}

export function paymentMethodRowToMandateAccount(
  row: InvestPaymentMethodType | null,
  mandateAccounts: AutoPayMandateDetails[],
  mode?: AutopayPaymentMode,
  bankAccounts?: AutoPayBankAccount[] | null
): AutoPayMandateDetails | null {
  if (!row?.paymentMethodId && !row?.displayAccNumber) return null

  const accountId =
    row.paymentMethodId ||
    bankAccounts?.find((account) => account?.displayAccNumber === row.displayAccNumber)?.accountId ||
    mandateAccounts.find((account) => account?.displayAccNumber === row.displayAccNumber)?.accountId

  if (!accountId) return null

  if (mode) {
    return findMandateAccountForAutopayMode(mandateAccounts, accountId, mode, bankAccounts)
  }

  const mandateAccount = mandateAccounts.find((account) => account?.accountId === accountId)
  if (mandateAccount) return mandateAccount

  const linkedBankAccount = bankAccounts?.find((account) => account?.accountId === accountId)
  if (linkedBankAccount) {
    return autoPayBankAccountToMandateDetails(linkedBankAccount)
  }

  return mandateAccounts.find((account) => account?.displayAccNumber === row.displayAccNumber) ?? null
}

/** True when the row has no mandate yet, or its mandate type matches the chosen payment mode. */
export function accountMandateTypeMatchesPaymentMode(
  account: AutoPayMandateDetails | null | undefined,
  mode: AutopayPaymentMode
): boolean {
  if (!account?.accountId) return false

  const accountMandateType = account.mandateType
  if (accountMandateType == null) {
    return true
  }

  const isUpiAccount = isUpiMandateType(accountMandateType)
  return mode === 'UPI' ? isUpiAccount : !isUpiAccount
}

/** Mandate id for setup only when the existing mandate matches the selected mode; otherwise null (new setup). */
export function resolveMandateIdForAutopayMode(
  account: AutoPayMandateDetails | null | undefined,
  mode: AutopayPaymentMode
): string | null {
  if (!account?.accountId) return null
  if (!accountMandateTypeMatchesPaymentMode(account, mode)) return null
  return account.mandateId ?? null
}

export function findMandateAccountForAutopayMode(
  mandateAccounts: AutoPayMandateDetails[],
  accountId: string,
  mode: AutopayPaymentMode,
  bankAccounts?: AutoPayBankAccount[] | null
): AutoPayMandateDetails | null {
  if (!accountId) return null

  const typeMatchedAccount = mandateAccounts.find(
    (account) => account?.accountId === accountId && accountMandateTypeMatchesPaymentMode(account, mode)
  )
  if (typeMatchedAccount) return typeMatchedAccount

  const mandateAccount = mandateAccounts.find((account) => account?.accountId === accountId)
  if (mandateAccount) {
    if (accountMandateTypeMatchesPaymentMode(mandateAccount, mode)) {
      return mandateAccount
    }

    // Same bank, opposite mandate type (e.g. UPI row when user chose net banking) — setup without mandateId.
    return {
      ...mandateAccount,
      mandateId: null,
      mandateType: null
    }
  }

  const linkedBankAccount = bankAccounts?.find((account) => account?.accountId === accountId)
  if (!linkedBankAccount) return null

  return autoPayBankAccountToMandateDetails(linkedBankAccount)
}

export function getSelectionIdFromAutopayMethod(
  selection: AutopayMethodSelection | null,
  installedUpiApps: InstalledUpiApp[] | null | undefined
): string | null {
  if (!selection) return null

  if (selection.mode === 'NET_BANKING') {
    return getAutopayNetBankingSelectionId(selection.accountId)
  }

  if (isAutopayUpiSelectionExcluded(selection)) return null

  const eligibleUpiApps = filterInstalledUpiAppsForAutopay(installedUpiApps)

  if (selection.upiPackageName) {
    return getUpiAppSelectionId({
      packageName: selection.upiPackageName,
      name: selection.upiAppName ?? '',
      appLogoUrl: selection.upiAppLogoUrl ?? ''
    })
  }

  const matchedApp = eligibleUpiApps.find((app) => app?.name === selection.upiAppName)
  if (matchedApp) {
    return getUpiAppSelectionId(matchedApp)
  }

  return null
}

export function buildAutopayMethodSelectionFromNetBanking(
  account: AutoPayMandateDetails
): AutopayMethodSelection | null {
  if (!account?.accountId) return null

  return {
    mode: 'NET_BANKING',
    accountId: account.accountId,
    mandateId: resolveMandateIdForAutopayMode(account, 'NET_BANKING'),
    displayAccNumber: account.displayAccNumber ?? '',
    bankUrlLogo: account.bankUrlLogo ?? ''
  }
}

export function buildAutopayMethodSelectionFromUpi(
  app: InstalledUpiApp,
  account: AutoPayMandateDetails
): AutopayMethodSelection | null {
  if (!account?.accountId) return null

  return {
    mode: 'UPI',
    accountId: account.accountId,
    mandateId: resolveMandateIdForAutopayMode(account, 'UPI'),
    displayAccNumber: account.displayAccNumber ?? '',
    bankUrlLogo: account.bankUrlLogo ?? '',
    upiPackageName: app?.packageName ?? null,
    upiAppName: app?.name ?? '',
    upiAppLogoUrl: app?.appLogoUrl ?? ''
  }
}

export function getSetupMandateTypeFromMandateAccount(
  account: AutoPayMandateDetails | null | undefined,
  requiredMandateType?: AutoPayMandateType | string | null
): SetupAutoPayMandateType {
  const mandateType = account?.mandateType ?? requiredMandateType
  return isUpiMandateType(mandateType) ? 'UPI_AUTOPAY' : 'NET_BANKING'
}

/**
 * Aligns selection with mandate-details defaults when none exists yet.
 * Honors explicit user mode (UPI vs net banking) even when SIP `mandateType` differs.
 * `bankAccounts` supplies default bank; `availableMandates` supplies typed mandate ids.
 */
export function reconcileAutopayMethodSelection({
  selection,
  mandateAccounts,
  bankAccounts,
  installedUpiApps,
  requiredMandateType,
  amount
}: {
  selection?: AutopayMethodSelection | null
  mandateAccounts: AutoPayMandateDetails[]
  bankAccounts?: AutoPayBankAccount[] | null
  installedUpiApps: InstalledUpiApp[] | null | undefined
  requiredMandateType?: AutoPayMandateType | string | null
  amount: number
}): AutopayMethodSelection | null {
  const eligibleUpiApps = filterInstalledUpiAppsForAutopay(installedUpiApps)
  const requiredMode = resolveAutopayPaymentMode({ mandateType: requiredMandateType, amount })
  const linkedBankAccounts = bankAccounts ?? []
  const matchedBankAccount = selection?.accountId
    ? linkedBankAccounts.find((item) => item?.accountId === selection.accountId)
    : null
  const matchedMandateAccount = selection?.accountId
    ? mandateAccounts.find((item) => item?.accountId === selection.accountId)
    : null
  const defaultBankAccount = linkedBankAccounts[0] ?? null
  const defaultAccountId = defaultBankAccount?.accountId ?? mandateAccounts[0]?.accountId ?? ''
  const defaultAccount = defaultAccountId
    ? findMandateAccountForAutopayMode(mandateAccounts, defaultAccountId, requiredMode, linkedBankAccounts)
    : null

  if (!defaultAccount?.accountId && !matchedBankAccount?.accountId) {
    return null
  }

  const enrichSelection = (value: AutopayMethodSelection): AutopayMethodSelection => {
    const accountForMode =
      findMandateAccountForAutopayMode(mandateAccounts, value.accountId, value.mode, linkedBankAccounts) ??
      defaultAccount

    if (!accountForMode?.accountId) {
      return value
    }

    return {
      ...value,
      mandateId: resolveMandateIdForAutopayMode(accountForMode, value.mode) ?? value.mandateId ?? null,
      displayAccNumber: value.displayAccNumber ?? accountForMode.displayAccNumber ?? '',
      bankUrlLogo: value.bankUrlLogo ?? accountForMode.bankUrlLogo ?? ''
    }
  }

  if (selection?.accountId && selection.mode && (matchedBankAccount || matchedMandateAccount)) {
    const mustUseNetBanking = requiredMode === 'NET_BANKING'
    // Drop excluded UPI apps so reconcile falls through to the next eligible default.
    if (!isAutopayUpiSelectionExcluded(selection) && (selection.mode === 'NET_BANKING' || !mustUseNetBanking)) {
      return enrichSelection(selection)
    }
  }

  if (!defaultAccount?.accountId) {
    return null
  }

  if (requiredMode === 'NET_BANKING') {
    return buildAutopayMethodSelectionFromNetBanking(defaultAccount)
  }

  const defaultApp = getDefaultInstalledUpiApp(eligibleUpiApps, null)
  if (defaultApp) {
    return buildAutopayMethodSelectionFromUpi(defaultApp, defaultAccount)
  }

  return buildAutopayMethodSelectionFromNetBanking(defaultAccount)
}

export function buildDefaultAutopayMethodSelection({
  amount,
  mandateAccounts,
  bankAccounts,
  installedUpiApps,
  preferredSelection,
  requiredMandateType
}: {
  amount: number
  mandateAccounts: AutoPayMandateDetails[]
  bankAccounts?: AutoPayBankAccount[] | null
  installedUpiApps: InstalledUpiApp[] | null | undefined
  preferredSelection?: AutopayMethodSelection | null
  requiredMandateType?: AutoPayMandateType | string | null
}): AutopayMethodSelection | null {
  return reconcileAutopayMethodSelection({
    selection: preferredSelection,
    mandateAccounts,
    bankAccounts,
    installedUpiApps,
    requiredMandateType,
    amount
  })
}

export function isAmountWithinUpiAutopayLimit(amount: number, upiAutoPayMaxLimit: number) {
  if (amount <= 0 || upiAutoPayMaxLimit <= 0) return true
  return amount < upiAutoPayMaxLimit
}

type ResolveConfirmableAutopayMethodArgs = {
  pendingSelection: AutopayMethodSelection | null
  selectedOptionId: string | null
  selectedBankAccount: InvestPaymentMethodType | null
  mandateAccounts: AutoPayMandateDetails[]
  bankAccounts?: AutoPayBankAccount[] | null
  installedUpiApps: InstalledUpiApp[] | null | undefined
}

export function resolveConfirmableAutopayMethod({
  pendingSelection,
  selectedOptionId,
  selectedBankAccount,
  mandateAccounts,
  bankAccounts,
  installedUpiApps
}: ResolveConfirmableAutopayMethodArgs): AutopayMethodSelection | null {
  // Ignore pending UPI choice when the app is excluded from autopay (product blocklist).
  if (pendingSelection?.accountId && !isAutopayUpiSelectionExcluded(pendingSelection)) {
    return pendingSelection
  }

  if (!selectedOptionId) return null

  if (selectedOptionId.startsWith('autopay-netbank:')) {
    const accountId = selectedOptionId.replace('autopay-netbank:', '')
    const account = findMandateAccountForAutopayMode(mandateAccounts, accountId, 'NET_BANKING', bankAccounts)
    return account ? buildAutopayMethodSelectionFromNetBanking(account) : null
  }

  if (!selectedOptionId.startsWith('upi:')) return null

  const eligibleUpiApps = filterInstalledUpiAppsForAutopay(installedUpiApps)
  const selectedApp = eligibleUpiApps.find((app) => getUpiAppSelectionId(app) === selectedOptionId)
  const mandateAccount = paymentMethodRowToMandateAccount(selectedBankAccount, mandateAccounts, 'UPI', bankAccounts)

  if (!selectedApp || !mandateAccount) return null

  return buildAutopayMethodSelectionFromUpi(selectedApp, mandateAccount)
}

export function isAutopayConfirmCtaEnabled(args: ResolveConfirmableAutopayMethodArgs) {
  return !!resolveConfirmableAutopayMethod(args)?.accountId
}

export function getAutopayModeLabel(mode: AutopayPaymentMode) {
  return mode === 'UPI' ? 'UPI' : 'Net Banking'
}

export function getSetupMandateTypeFromAutopaySelection(
  selection: AutopayMethodSelection | null | undefined
): SetupAutoPayMandateType {
  return selection?.mode === 'UPI' ? 'UPI_AUTOPAY' : 'NET_BANKING'
}

/**
 * POST .../mandate from SetupAutoPay. `mandateType` follows user selection; `mandateId` only when
 * `availableMandates` has a row matching account + mode (empty string triggers new mandate setup).
 */
export function buildSetupAutoPayPayloadFromSelection({
  sxpId,
  selection,
  mandateAccounts,
  bankAccounts
}: {
  sxpId: string
  selection: AutopayMethodSelection
  mandateAccounts?: AutoPayMandateDetails[]
  bankAccounts?: AutoPayBankAccount[] | null
}): SetupAutoPayPayload | null {
  if (!sxpId || !selection?.accountId) return null

  const account = mandateAccounts?.length
    ? findMandateAccountForAutopayMode(mandateAccounts, selection.accountId, selection.mode, bankAccounts)
    : null

  return {
    sxpId,
    accountId: selection.accountId,
    mandateId: account ? (resolveMandateIdForAutopayMode(account, selection.mode) ?? '') : (selection.mandateId ?? ''),
    mandateType: getSetupMandateTypeFromAutopaySelection(selection)
  }
}

/** POST .../mandate payload when mandate-details already has a matching available mandate. */
export function buildSetupAutoPayPayloadFromMatchingMandate({
  sxpId,
  matchingMandate,
  mandateDetails
}: {
  sxpId: string
  matchingMandate: AutoPayMandateDetails
  mandateDetails?: MandateDetailsResponse | null
}): SetupAutoPayPayload | null {
  if (!sxpId || !matchingMandate?.accountId) {
    return null
  }

  return {
    sxpId,
    accountId: matchingMandate.accountId,
    mandateId: matchingMandate.mandateId ?? '',
    mandateType: getSetupMandateTypeFromMandateAccount(matchingMandate, mandateDetails?.mandateType)
  }
}

/** Minimal autopay selection for post-setup navigation (status / link-waiting URLs). */
export function buildAutopayMethodSelectionFromMatchingMandate(
  matchingMandate: AutoPayMandateDetails,
  mandateDetails: MandateDetailsResponse | null | undefined,
  upiPackageName?: string | null
): AutopayMethodSelection | null {
  if (!matchingMandate?.accountId) {
    return null
  }

  const isUpiMandate = isUpiMandateType(matchingMandate?.mandateType ?? mandateDetails?.mandateType)

  if (isUpiMandate && upiPackageName) {
    return {
      mode: 'UPI',
      accountId: matchingMandate.accountId,
      mandateId: matchingMandate.mandateId,
      displayAccNumber: matchingMandate.displayAccNumber ?? '',
      bankUrlLogo: matchingMandate.bankUrlLogo ?? '',
      upiPackageName
    }
  }

  return buildAutopayMethodSelectionFromNetBanking(matchingMandate)
}

export function buildAutoPayStatusSearchParams({
  sxpId,
  mandateId,
  autopayMethod
}: {
  sxpId: string
  mandateId: string
  autopayMethod?: AutopayMethodSelection | null
}) {
  const params = new URLSearchParams({ sxpId, mandateId })

  // Backend needs the native package id to drive UPI intent during mandate status polling.
  if (autopayMethod?.mode === 'UPI' && autopayMethod?.upiPackageName) {
    params.set('upiPackageName', autopayMethod.upiPackageName)
  }

  return params
}

/**
 * Post-setup waiting route: UPI autopay uses terminal status + intent (`AutoPayUpiLinkWaitingPage`);
 * net banking keeps the legacy redirect page (`AutoPayLinkWaitingPage`).
 */
export function buildAutoPayLinkWaitingPath({
  sxpId,
  mandateId,
  autopayMethod
}: {
  sxpId: string
  mandateId: string
  autopayMethod?: AutopayMethodSelection | null
}) {
  const params = buildAutoPayStatusSearchParams({ sxpId, mandateId, autopayMethod })
  if (autopayMethod?.mode === 'UPI') {
    return `/sip/auto-pay/upi/link-waiting?${params.toString()}`
  }
  return `/sip/auto-pay/link-waiting?${params.toString()}`
}
